import { ActionModal } from '@kcftech/design-system';
import { AssetClassificationTypeCreateRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Filter } from 'utils/query_v3';
import { appendToRoute } from 'utils/RouteHelper';

export type CreateAssetTypeProps = Readonly<{
    onClose: (saved: boolean) => void;
}>;

type CreateAssetTypeForm = Readonly<{
    name: string;
}>;

const maxAssetTypeNameLength = 50;

const nameRules: RegisterOptions = {
    required: 'Please enter the asset type name',
    maxLength: {
        value: maxAssetTypeNameLength,
        message: `Asset type name cannot be greater than ${maxAssetTypeNameLength} characters`,
    },
};

export function CreateAssetTypeModal({ onClose }: CreateAssetTypeProps) {
    const history = useHistory();
    const { showToast } = useNotificationContext();
    const { url } = useRouteMatch();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateAssetTypeForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    const { mutate: createAssetClassificationType, isPending } = useMutation({
        meta: { name: 'create-asset-classification-type' },
        mutationFn: async (request: AssetClassificationTypeCreateRequest) =>
            sdClient.createAssetClassificationType({ body: request }),

        onSuccess: (response) => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully created asset type',
            });
            history.push(appendToRoute(url, response.Id));
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to create asset type' }),
    });

    const onSave = handleSubmit((formData) =>
        createAssetClassificationType(
            new AssetClassificationTypeCreateRequest({
                Name: formData.name.trim(),
            }),
        ),
    );

    const validateName = async (value: string) => {
        const assetTypeNameFiltered = await sdClient.getAssetClassificationTypes({
            filter: Filter.eq('Name', value),
        });

        if (value.trim() === '') {
            return 'Please enter the asset type name';
        }

        if (assetTypeNameFiltered.Result.length >= 1) {
            return 'Name already exists. Please enter a unique name.';
        }

        return undefined;
    };

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(false)}
            title="Create Asset Type"
            height={350}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <ValidatableLabel
                ariaLabel="create asset type name label"
                label="Name"
                error={errors.name}
                required
                className="mb-20"
            >
                <TextInput
                    autoFocus
                    placeholder="Enter a name for the asset type"
                    invalid={errors.name !== undefined}
                    {...register('name', { ...nameRules, validate: validateName })}
                />
            </ValidatableLabel>
        </ActionModal>
    );
}
